import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from '../../components/ui/Button';
import { api } from '../../lib/api';
import { useSession } from '../../stores/session';
import { Character } from './Character';
import {
  DEFAULT,
  OPTIONS,
  PALETTE,
  fromSeed,
  normalise,
  type Character as CharacterConfig,
} from './config';

/**
 * Building the character other people see you as.
 *
 * ## It opens on somebody rather than on nobody
 *
 * A blank form asking for nineteen hairstyles is work. Everybody already has a
 * character seeded from their username - it is what the crowd has been drawing
 * for them - so the maker starts there and the job becomes changing what you do
 * not like, which is a far smaller thing to ask of a person.
 *
 * ## Saved on purpose, not on every click
 *
 * Unlike the switches elsewhere in settings, this is a dozen decisions made in
 * one sitting. Saving each one would put a request behind every hairstyle
 * somebody tried on, and leave whoever closed the tab halfway through wearing
 * something they were only looking at.
 *
 * ## Randomise is the good button
 *
 * Most people will press it more than they will use the pickers, and somebody
 * who presses it four times and saves has still made something of their own -
 * which is the whole point. It is deliberately not the seeded character: that
 * one they already have, and rolling it again would look broken.
 *
 * ## Steppers, not a wall of chips
 *
 * Every choice laid out as chips was a column six screens long, and the preview
 * scrolled off the top of it - so people were changing a hairstyle they could
 * not see. A stepper is one line whatever the list costs, which is what lets
 * the whole character fit beside its own picture.
 *
 * The trade is that you cannot see the options without walking them. That is
 * the right way round here: nobody arrives knowing they want "mutton chops",
 * and the thing they actually do is press until something looks good. The
 * admin rig keeps the chips and the contact sheet for the other job - checking
 * that every part sits right - which is a different question asked by a
 * different person.
 */

/**
 * What belongs with what.
 *
 * Colour used to be five swatch rows in a block at the bottom, so choosing hair
 * meant picking a style near the top and its colour four groups later. Grouped
 * by the part of a person they describe, the pair is one glance apart.
 */
const GROUPS = [
  {
    label: 'Face',
    parts: [
      ['expr', 'Expression'],
      ['face', 'Facial hair'],
    ],
    colours: [
      ['skinColor', 'Skin'],
      ['eyeColor', 'Eyes'],
    ],
  },
  {
    label: 'Hair',
    parts: [['hair', 'Style']],
    colours: [['hairColor', 'Colour']],
  },
  {
    label: 'Clothes',
    parts: [
      ['outfit', 'Top'],
      ['legs', 'Bottom'],
    ],
    colours: [
      ['outfitColor', 'Colour'],
      ['shoeColor', 'Shoes'],
    ],
  },
  {
    label: 'Extras',
    parts: [
      ['acc', 'On the head'],
      ['item', 'Holding'],
    ],
    colours: [],
  },
] as const;

export function CharacterMaker() {
  const user = useSession((state) => state.user);
  const refreshUser = useSession((state) => state.refreshUser);

  const seeded = fromSeed(user?.username ?? 'getvibed');
  const [draft, setDraft] = useState<CharacterConfig>(
    user?.character ? normalise(user.character) : seeded,
  );
  const [saved, setSaved] = useState(false);

  const save = useMutation({
    mutationFn: (character: CharacterConfig | null) => api.patch('/me', { character }),
    onSuccess: async () => {
      await refreshUser();
      setSaved(true);
    },
  });

  const set = (patch: Partial<CharacterConfig>) => {
    setSaved(false);
    setDraft((current) => ({ ...current, ...patch }));
  };

  const randomise = () => {
    // Seeded off the clock rather than off the username, so pressing it twice
    // gives two characters - the name-seeded one never changes by design.
    set(fromSeed(`${Math.random()}`));
  };

  return (
    <div className="flex flex-col gap-6 sm:flex-row sm:gap-7">
      {/*
        Sticky, because the controls are taller than the panel and the whole
        point of a preview is being able to see it while you press things.
      */}
      <div className="flex shrink-0 flex-col items-center gap-3 sm:sticky sm:top-0 sm:self-start">
        <div className="rounded-[var(--radius-card)] bg-raised p-3">
          <Character config={draft} size={168} crop="full" />
        </div>

        <div className="flex w-full gap-2">
          <Button variant="secondary" className="flex-1" data-character-random onClick={randomise}>
            Randomise
          </Button>

          <Button
            className="flex-1"
            data-character-save
            disabled={save.isPending}
            onClick={() => save.mutate(draft)}
          >
            {saved ? 'Saved' : 'Save'}
          </Button>
        </div>

        {user?.character != null && (
          /*
           * Back to the character their name makes, rather than to a fixed
           * default nobody chose. Stored as null, so they follow the generator
           * as it changes instead of being frozen to today's output.
           */
          <button
            type="button"
            data-character-reset
            onClick={() => {
              setDraft(seeded);
              save.mutate(null);
            }}
            className="meta text-faint transition-colors hover:text-soft"
          >
            Use the one from my name
          </button>
        )}
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-5">
        {GROUPS.map((group) => (
          <section key={group.label} className="flex flex-col gap-2.5" data-character-group={group.label}>
            <h3 className="meta-caps border-b border-line pb-1.5 text-soft">{group.label}</h3>

            {/*
              Two across wherever there is room. A stepper is a fixed height
              whatever it holds, so the pairs line up instead of leaving the
              ragged edge a wrapped row of chips always had.
            */}
            <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2">
              {group.parts.map(([key, label]) => (
                <Stepper
                  key={key}
                  name={key}
                  label={label}
                  options={OPTIONS[key]}
                  value={draft[key]}
                  onPick={(value) => set({ [key]: value } as Partial<CharacterConfig>)}
                />
              ))}

              {group.colours.map(([key, label]) => (
                <Swatches
                  key={key}
                  name={key}
                  label={label}
                  colours={PALETTE[key]}
                  value={draft[key]}
                  onPick={(value) => set({ [key]: value } as Partial<CharacterConfig>)}
                />
              ))}

              {group.label === 'Extras' && <Toggles draft={draft} onSet={set} />}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

/**
 * One choice out of a list, one line tall.
 *
 * It wraps at both ends rather than disabling an arrow at the edge, because a
 * dead button on the last hairstyle reads as broken far more often than it
 * reads as "that is the end" - and there is no meaningful first or last here,
 * only a ring you walk round.
 */
function Stepper({
  name,
  label,
  options,
  value,
  onPick,
}: {
  name: string;
  label: string;
  options: readonly (readonly [string, string])[];
  value: string;
  onPick: (value: string) => void;
}) {
  const at = options.findIndex(([option]) => option === value);
  const here = at === -1 ? 0 : at;

  const step = (by: number) => {
    const next = options[(here + by + options.length) % options.length];

    if (next) onPick(next[0]);
  };

  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <span className="meta-caps text-faint">{label}</span>

      <div
        role="group"
        aria-label={label}
        data-character-stepper={name}
        data-value={value}
        className="flex items-center rounded-[var(--radius-pill)] border border-line bg-app transition-colors focus-within:border-line-strong hover:border-line-strong"
      >
        <Arrow label={`Previous ${label.toLowerCase()}`} attr={`prev-${name}`} onClick={() => step(-1)} back />

        {/*
          The name of the thing, and a count.

          Nineteen hairstyles behind one arrow is otherwise a well with no
          bottom - the count is what says how far there is to go, and how far
          round you already are.
        */}
        <span className="flex min-w-0 flex-1 items-baseline justify-center gap-1.5 px-1">
          <span className="truncate text-sm text-ink">{options[here]?.[1] ?? value}</span>
          <span className="shrink-0 font-mono text-[10px] text-ghost">
            {here + 1}/{options.length}
          </span>
        </span>

        <Arrow label={`Next ${label.toLowerCase()}`} attr={`next-${name}`} onClick={() => step(1)} />
      </div>
    </div>
  );
}

function Arrow({
  label,
  attr,
  onClick,
  back = false,
}: {
  label: string;
  attr: string;
  onClick: () => void;
  back?: boolean;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      data-character-step={attr}
      onClick={onClick}
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-pill)] text-soft transition-colors hover:bg-raised hover:text-ink"
    >
      <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path
          d={back ? 'M10 3.5 5.5 8l4.5 4.5' : 'M6 3.5 10.5 8 6 12.5'}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}

/**
 * Colour stays a row of swatches.
 *
 * A stepper is the right shape for a list you read the names of; a palette is a
 * list you do not - "Colour 4 of 9" tells nobody anything, and the swatches
 * are already small enough to sit on one line.
 */
function Swatches({
  name,
  label,
  colours,
  value,
  onPick,
}: {
  name: string;
  label: string;
  colours: readonly string[];
  value: string;
  onPick: (value: string) => void;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <span className="meta-caps text-faint">{label}</span>

      <div className="flex min-h-8 flex-wrap items-center gap-1" data-character-swatches={name}>
        {colours.map((colour) => (
          <button
            key={colour}
            type="button"
            aria-label={colour}
            aria-pressed={value === colour}
            data-character-colour={colour}
            onClick={() => onPick(colour)}
            style={{ background: colour }}
            className={`h-[22px] w-[22px] rounded-[var(--radius-pill)] border-2 transition-transform ${
              value === colour ? 'border-ink scale-110' : 'border-line hover:scale-105'
            }`}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * The three that are on or off.
 *
 * Chips, not steppers: a stepper for two states is a control you have to press
 * to find out what it does, and "Chain 1/2" is a worse way of saying no.
 */
function Toggles({
  draft,
  onSet,
}: {
  draft: CharacterConfig;
  onSet: (patch: Partial<CharacterConfig>) => void;
}) {
  const items = [
    ['wrist', 'Wristband'],
    ['chain', 'Chain'],
    ['ear', 'Earrings'],
  ] as const;

  return (
    <div className="flex min-w-0 flex-col gap-1.5 sm:col-span-2">
      <span className="meta-caps text-faint">Wearing</span>

      <div className="flex min-h-8 flex-wrap items-center gap-1.5">
        {items.map(([key, label]) => (
          <button
            key={key}
            type="button"
            aria-pressed={draft[key]}
            data-character-extra={key}
            onClick={() => onSet({ [key]: !draft[key] } as Partial<CharacterConfig>)}
            className={`meta rounded-[var(--radius-pill)] border px-2.5 py-1 transition-colors ${
              draft[key]
                ? 'border-transparent bg-accent text-accent-ink'
                : 'border-line text-soft hover:border-line-strong hover:text-ink'
            }`}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Re-exported so callers do not need to know where the default lives. */
export { DEFAULT as DEFAULT_CHARACTER };
